import { gql } from '@apollo/client';

const TaskFields = gql`
  fragment TaskFields on Task {
    taskId
    taskName
    createdDate
    dueDate
    initiatorName
    assigneeName
    processDefinitionKey
    approvalStatus
    ownerId
    ownerName
    processDefinitionName
    taskNumber
    assigneeId
    initiatorId
    processInstanceId
    requestType
    groupId
    businessKey
    endDate
    slackChannel
    gsacId
    entityType
    slackMessageUrl
    entityId
    discountApprovalRequiredInPricingPlan
    discountValue
  }
`;

export const fetchTaskList = gql`
  query fetchTaskList(
    $staffId: ID!
    $taskType: TaskType!
    $taskStatus: TaskStatus
    $businessKey: String
    $approvalType: ApprovalType
  ) {
    taskList(
      input: {
        staffId: $staffId
        taskType: $taskType
        taskStatus: $taskStatus
        businessKey: $businessKey
        approvalType: $approvalType
      }
    ) {
      tasks {
        ...TaskFields
      }
      userRole
    }
  }
  ${TaskFields}
`;

export const fetchGroupMembers = gql`
  query fetchGroupMembers($staffId: ID!, $groupName: String, $requestType: String) {
    groupMembers(input: { staffId: $staffId, groupName: $groupName, requestType: $requestType }) {
      userId
      name
    }
  }
`;

export const fetchTaskComments = gql`
  query fetchTaskComments($taskId: ID!, $id: ID!) {
    comments(taskId: $taskId, id: $id) {
      id
      userId
      message
      time
    }
  }
`;

export const TaskDetailEvaluatedEntityInformation = gql`
  fragment TaskDetailEvaluatedEntityInformation on EvaluatedEntityInformation {
    entityId
    entityType
    noOfDays
    refundType
    approvalRequired
    slackChannel
    approverGroup
    currentTotalTrialLength
    trialExtensionDaysRequested
    netTrialDays
    currentTotalQuoteExtensionLength
    quoteExtensionDaysRequested
    netQuoteExtensionDays
    discountValue
    discountPercentage
    toPricingPlanKey
    fromPricingPlanKey
    cloudSiteName
    cloudSiteUrl
  }
`;

export const fetchUserRole = gql`
  query fetchUserRole($id: ID!) {
    userRole(staffId: $id)
  }
`;

export const fetchTaskDetail = gql`
  query fetchTaskDetail($taskId: ID!, $id: ID!) {
    taskDetail(taskId: $taskId, id: $id) {
      taskProperties {
        id
        name
        assignee
        created
        owner
        processDefinitionId
        processInstanceId
        taskDefinitionKey
      }
      businessKey
      assigneeName
      ownerName
      taskVariables {
        approvalStatus
        taskNumber
        slackMessageUrl
      }
      processVariables {
        initiatorName
        initiatorId
        initiatorComments
        requestType
        gsacId
        discountApprovalRequiredInPricingPlan
        evaluatedEntityInformation {
          ...TaskDetailEvaluatedEntityInformation
        }
        approvalEntityResult {
          ...TaskDetailEvaluatedEntityInformation
        }
      }
    }
    userRole(staffId: $id)
  }
  ${TaskDetailEvaluatedEntityInformation}
`;

export const fetchTaskActivityLog = gql`
  query fetchTaskActivityLog($taskId: ID!, $id: ID!) {
    taskActivityLog(taskId: $taskId, id: $id) {
      taskName
      taskId
      taskNumber
      taskStatus
      taskAuditTrail {
        time
        context
        actor
        value
      }
    }
  }
`;

export const updateTask = gql`
  mutation updateTask($taskId: ID!, $approvalStatus: ApprovalStatus, $comment: String, $id: ID!) {
    updateTask(input: { taskId: $taskId, approvalStatus: $approvalStatus, comment: $comment, id: $id }) {
      status
    }
  }
`;

export const fetchAuditTrailList = gql`
  query fetchAuditTrailList($id: ID!, $businessKey: String) {
    auditTrailTaskList(id: $id, businessKey: $businessKey) {
      tasks {
        taskName
        taskId
        taskAuditTrail {
          time
          context
          actor
          comments
        }
      }
    }
  }
`;

export const assignTask = gql`
  mutation assignTask($taskId: ID!, $assignee: String, $id: ID!) {
    assignTask(input: { taskId: $taskId, assignee: $assignee, id: $id }) {
      status
    }
  }
`;

export const unAssignTask = gql`
  mutation unAssignTask($taskId: ID!, $id: ID!) {
    unAssignTask(taskId: $taskId, id: $id) {
      status
    }
  }
`;

export const cancelRequest = gql`
  mutation cancelRequest($processInstanceId: String, $id: ID!, $businessKey: String, $comment: String) {
    cancelRequest(
      input: { processInstanceId: $processInstanceId, id: $id, businessKey: $businessKey, comment: $comment }
    ) {
      status
    }
  }
`;

export const changeAssignee = gql`
  mutation changeAssignee(
    $assignee: String
    $groupName: String
    $requestType: String
    $id: ID!
    $taskIdList: [String]
  ) {
    changeAssignee(
      input: { assignee: $assignee, groupName: $groupName, requestType: $requestType, id: $id, taskIdList: $taskIdList }
    ) {
      status
    }
  }
`;

export const bulkTaskUpdate = gql`
  mutation bulkTaskUpdate($input: BulkTaskUpdateInput!, $id: ID!) {
    bulkTaskUpdate(input: $input, id: $id) {
      status
      taskList {
        taskId
        updated
        errorMessage
      }
    }
  }
`;
